import { RedoConciergeRpcClientContext } from "@redotech/redo-merchant-app-common/concierge-rpc-client-provider";
import { useContext, useEffect, useState } from "react";
import type { KnowledgeSource } from "./suggested-reply-card";

interface UseKnowledgeSourcesParams {
  /**
   * The kind of message/draft we're fetching sources for
   */
  input:
    | { kind: "v1_message"; conversationId: string; customerMessageId: string }
    | { kind: "v2_draft"; draftMessageId: string }
    | { kind: "v2_message"; messageId: string }
    | null;
  /**
   * Whether to fetch the sources (e.g., only when there's a suggestion)
   */
  enabled: boolean;
}

interface UseKnowledgeSourcesResult {
  sources: KnowledgeSource[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch AI knowledge sources used for generating a copilot response.
 */
export function useKnowledgeSources({
  input,
  enabled,
}: UseKnowledgeSourcesParams): UseKnowledgeSourcesResult {
  const conciergeRpcClient = useContext(RedoConciergeRpcClientContext);
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Create a stable key for the input to use in the dependency array
  const inputKey = input
    ? input.kind === "v1_message"
      ? `v1:${input.conversationId}:${input.customerMessageId}`
      : input.kind === "v2_draft"
        ? `v2_draft:${input.draftMessageId}`
        : `v2_message:${input.messageId}`
    : null;

  useEffect(() => {
    if (!enabled || !input || !conciergeRpcClient) {
      setSources([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    conciergeRpcClient
      .getKnowledgeSourcesForMessage(input)
      .then((result) => {
        if (!cancelled) {
          setSources(result.sources);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to fetch knowledge sources:", err);
          setError(err instanceof Error ? err : new Error(String(err)));
          setSources([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, inputKey, conciergeRpcClient]);

  return { sources, loading, error };
}
